import {
	type GetPerformanceStatsQuery,
	type GetQuoteFunnelStatsQuery,
	PerformanceComparison,
	PerformanceGranularity,
	PerformancePeriod,
} from '@codezo/graphql';

export type PerformanceStats = GetPerformanceStatsQuery['getPerformanceStats'];
export type Bucket = PerformanceStats['buckets'][number];
export type FunnelStats = GetQuoteFunnelStatsQuery['getQuoteFunnelStats'];

export const PERIOD_BY_PARAM: Record<string, PerformancePeriod> = {
	month: PerformancePeriod.Month,
	quarter: PerformancePeriod.Quarter,
	year: PerformancePeriod.Year,
	all: PerformancePeriod.AllTime,
};

export const PERIOD_OPTIONS: [string, string][] = [
	['month', 'This month'],
	['quarter', 'Last 13 weeks'],
	['year', 'Last 12 months'],
	['all', 'All time'],
];

export const COMPARE_BY_PARAM: Record<string, PerformanceComparison> = {
	prev: PerformanceComparison.PreviousPeriod,
	yoy: PerformanceComparison.SamePeriodLastYear,
	none: PerformanceComparison.None,
};

export const COMPARE_LABELS: Record<PerformanceComparison, string> = {
	[PerformanceComparison.PreviousPeriod]: 'Previous period',
	[PerformanceComparison.SamePeriodLastYear]: 'Same period last year',
	[PerformanceComparison.None]: 'No comparison',
};

/* ── Dates. The backend sends calendar days as midnight UTC, so they are formatted in UTC. ── */

const fmt = (opts: Intl.DateTimeFormatOptions) =>
	new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', ...opts });
const dayMonth = fmt({ day: 'numeric', month: 'short' });
const dayMonthYear = fmt({ day: 'numeric', month: 'short', year: 'numeric' });
const monthShort = fmt({ month: 'short' });
const monthYear = fmt({ month: 'short', year: 'numeric' });

// en-GB writes September as "Sept"; the rest of the dashboard says "Sep".
const tidy = (s: string) => s.replace('Sept', 'Sep');

export function rangeLabel(start: string, end: string): string {
	const a = new Date(start);
	const b = new Date(end);
	if (a.getUTCFullYear() !== b.getUTCFullYear()) {
		return tidy(`${dayMonthYear.format(a)} – ${dayMonthYear.format(b)}`);
	}
	if (a.getUTCMonth() === b.getUTCMonth()) {
		return tidy(`${a.getUTCDate()} – ${dayMonthYear.format(b)}`);
	}
	return tidy(`${dayMonth.format(a)} – ${dayMonthYear.format(b)}`);
}

export const sinceLabel = (iso: string) => tidy(`Since ${monthYear.format(new Date(iso))}`);

/** ISO week number of a Monday-start bucket. */
function isoWeek(d: Date): number {
	const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 3));
	const firstThursday = new Date(Date.UTC(t.getUTCFullYear(), 0, 4));
	return 1 + Math.round((t.getTime() - firstThursday.getTime()) / (7 * 864e5));
}

/** Axis label for bucket i — sparse, so a 31-day axis never crowds. */
export function bucketLabel(
	b: Bucket,
	i: number,
	all: Bucket[],
	granularity: PerformanceGranularity,
	todayIndex: number
): string {
	const d = new Date(b.start);
	const last = i === all.length - 1;
	switch (granularity) {
		case PerformanceGranularity.Day: {
			const n = d.getUTCDate();
			if (i === todayIndex || last) return String(n);
			// A regular tick right beside today or the month's end would crowd it.
			const crowds = Math.abs(i - todayIndex) <= 2 || all.length - 1 - i <= 2;
			return !crowds && (n === 1 || n % 5 === 0) ? String(n) : '';
		}
		case PerformanceGranularity.Week:
			return i % 3 === 0 || last ? `W${isoWeek(d)}` : '';
		case PerformanceGranularity.Month: {
			const m = d.getUTCMonth();
			const withYear = m === 0 || i === 0;
			// Past a year of bars, every label won't fit: keep the first, each January and quarters.
			if (all.length > 12 && !withYear && !last && (m % 3 !== 0 || i === 1)) return '';
			return tidy(
				withYear
					? `${monthShort.format(d)} ’${String(d.getUTCFullYear()).slice(2)}`
					: monthShort.format(d)
			);
		}
		default:
			return String(d.getUTCFullYear());
	}
}

export function bucketName(b: Bucket, granularity: PerformanceGranularity): string {
	const d = new Date(b.start);
	switch (granularity) {
		case PerformanceGranularity.Day:
			return tidy(dayMonth.format(d));
		case PerformanceGranularity.Week:
			return tidy(`Week ${isoWeek(d)} · from ${dayMonth.format(d)}`);
		case PerformanceGranularity.Month:
			return tidy(monthYear.format(d));
		default:
			return String(d.getUTCFullYear());
	}
}

/* ── Numbers ── */

export function compactMoney(amount: number, currency: string): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency,
		notation: 'compact',
		maximumFractionDigits: amount >= 1e6 ? 2 : 1,
	}).format(amount);
}

export function money(amount: number, currency: string): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency,
		maximumFractionDigits: 0,
	}).format(amount);
}

export const pctText = (n: number | null | undefined) => (n == null ? '—' : `${n.toFixed(1)}%`);

/** A round top for an axis a bit above `max`, so gridlines land on readable numbers. */
export function niceTop(max: number): number {
	if (max <= 0) return 1;
	const mag = 10 ** Math.floor(Math.log10(max));
	return Math.ceil((max * 1.08) / (mag / 2)) * (mag / 2);
}

export type Delta = { text: string; tone: 'up' | 'down' | 'flat' } | null;

/** Relative change in percent, or null when there is nothing to compare with. */
export function percentDelta(
	now: number | null | undefined,
	before: number | null | undefined
): Delta {
	if (now == null || before == null || before === 0) return null;
	const d = Math.round(((now - before) / before) * 100);
	return {
		text: d === 0 ? 'No change' : `${d > 0 ? '↑' : '↓'} ${Math.abs(d)}%`,
		tone: d > 0 ? 'up' : d < 0 ? 'down' : 'flat',
	};
}

/** Change in percentage points, for rates. */
export function pointDelta(
	now: number | null | undefined,
	before: number | null | undefined
): Delta {
	if (now == null || before == null) return null;
	const d = Math.round((now - before) * 10) / 10;
	return {
		text: d === 0 ? 'No change' : `${d > 0 ? '↑' : '↓'} ${Math.abs(d).toFixed(1)} pt`,
		tone: d > 0 ? 'up' : d < 0 ? 'down' : 'flat',
	};
}

/* ── Month view: cumulative revenue and a weekday run-rate projection ── */

export const cumulative = (buckets: Bucket[]) =>
	buckets.reduce<number[]>(
		(acc, b) => [...acc, (acc.length ? acc[acc.length - 1] : 0) + b.revenue],
		[]
	);

const isWeekday = (iso: string) => {
	const w = new Date(iso).getUTCDay();
	return w !== 0 && w !== 6;
};

/**
 * Month-to-date revenue carried to the end of the month at this month's pace per working day.
 * Weekends add nothing — orders are accepted on working days — so a month that has had its
 * weekends early isn't over-projected. Null until a working day has passed.
 */
export function projectMonth(buckets: Bucket[]): number[] | null {
	const cum = cumulative(buckets);
	const todayIndex = buckets.findIndex((b) => b.future) - 1;
	const lastKnown = todayIndex < 0 ? buckets.length - 1 : todayIndex;
	const worked = buckets.slice(0, lastKnown + 1).filter((b) => isWeekday(b.start)).length;
	if (!worked) return null;
	const pace = cum[lastKnown] / worked;
	return buckets.map((b, i) => {
		if (i <= lastKnown) return cum[i];
		const ahead = buckets.slice(lastKnown + 1, i + 1).filter((x) => isWeekday(x.start)).length;
		return cum[lastKnown] + ahead * pace;
	});
}
